import { BeheerVestiging, Openingstijden, useCreateVestiging, useUpdateVestiging } from "@/entities/vestiging";
import { VestigingBeschikbareFaciliteiten } from "@/entities/vestiging/constants";
import { StationsAutocomplete } from "@/features/infra";
import {
    Button,
    Checkbox,
    CheckboxFormField,
    DialogBody,
    DialogFooter,
    Form,
    FormFieldInput,
    FormFieldInputTime,
    Label,
    TextAreaFormField,
} from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { UpsertVestigingDto } from "@/shared/types";
import { timeRegex } from "@/shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { SubmitHandler, useForm, UseFormReturn } from "react-hook-form";
import { z } from "zod";

const DAYS = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"] as const;

const hoursMinutesString = z.string().trim().regex(timeRegex, { error: "Gebruik het formaat HH:MM" });

const newEditVestigingSchema = z.object({
    stationCode: z.string().trim().nonempty({ error: "Kies een station." }),
    bedrijfsNaam: z.string().trim().nonempty({ error: "Vul de bedrijfsnaam in." }),
    vestigingNaam: z.string().trim().nonempty({ error: "Vul de vestigingnaam in." }),
    heeftKoffieThee: z.boolean(),
    heeftToilet: z.boolean(),
    isNsRetailVestiging: z.boolean(),
    inzetbaar: z.boolean(),
    telefoonnummer: z
        .string()
        .trim()
        .regex(/^\d{10}$/, { error: "Telefoonnummer moet precies 10 cijfers bevatten." })
        .optional()
        .or(z.literal("")),
    opmerkingVestiging: z.string().trim().optional(),
    openMaandag: hoursMinutesString,
    sluitMaandag: hoursMinutesString,
    openDinsdag: hoursMinutesString,
    sluitDinsdag: hoursMinutesString,
    openWoensdag: hoursMinutesString,
    sluitWoensdag: hoursMinutesString,
    openDonderdag: hoursMinutesString,
    sluitDonderdag: hoursMinutesString,
    openVrijdag: hoursMinutesString,
    sluitVrijdag: hoursMinutesString,
    openZaterdag: hoursMinutesString,
    sluitZaterdag: hoursMinutesString,
    openZondag: hoursMinutesString,
    sluitZondag: hoursMinutesString,
});

type NewVestigingFormValues = z.infer<typeof newEditVestigingSchema>;

interface OpeningHoursDayProps {
    form: UseFormReturn<NewVestigingFormValues>;
    day: (typeof DAYS)[number];
    handmatigToegevoegd: boolean;
}

export function OpeningHoursDay({ form, day, handmatigToegevoegd }: Readonly<OpeningHoursDayProps>) {
    const openFieldName = `open${day}` as const;
    const sluitFieldName = `sluit${day}` as const;

    const openValue = form.getValues(openFieldName);
    const sluitValue = form.getValues(sluitFieldName);

    const [isOpen, setIsOpen] = useState(!!openValue && !!sluitValue && openValue !== "Gesloten" && sluitValue !== "Gesloten");

    const handleToggle = (checked: boolean | "indeterminate") => {
        const isChecked = checked === true;
        setIsOpen(isChecked);

        if (isChecked) {
            form.setValue(openFieldName, "08:00");
            form.setValue(sluitFieldName, "17:00");
        } else {
            form.setValue(openFieldName, "Gesloten");
            form.setValue(sluitFieldName, "Gesloten");
        }
    };

    return (
        <>
            <Label className="col-span-3 col-start-1 flex items-center gap-3 justify-self-end pr-4 text-right">
                {day}
                <Checkbox checked={isOpen} onCheckedChange={handleToggle} disabled={!handmatigToegevoegd} />
            </Label>
            <FormFieldInputTime
                className="col-span-2 col-start-4"
                form={form}
                name={openFieldName}
                defaultValue="08:00"
                readOnly={!isOpen || !handmatigToegevoegd}
            />
            <FormFieldInputTime
                className="col-span-2 col-start-6"
                form={form}
                name={sluitFieldName}
                defaultValue="17:00"
                readOnly={!isOpen || !handmatigToegevoegd}
            />
            <div className="col-span-3"></div>
        </>
    );
}

interface NewEditVestigingenFormProps {
    onOpenChange: (open: boolean) => void;
    vestiging: BeheerVestiging | null;
}

export function NewEditVestigingForm({ onOpenChange, vestiging }: Readonly<NewEditVestigingenFormProps>) {
    const createVestiging = useCreateVestiging();
    const updateVestiging = useUpdateVestiging();
    const isEditing = Boolean(vestiging);
    const handmatigToegevoegd = vestiging?.handmatigToegevoegd ?? true; // New vestigingen are handmatig by default
    const [submitError, setSubmitError] = useState<Error | null>(null);

    const form = useForm<z.infer<typeof newEditVestigingSchema>>({
        resolver: zodResolver(newEditVestigingSchema),
        defaultValues: {
            stationCode: "",
            bedrijfsNaam: vestiging?.bedrijfsNaam ?? "",
            vestigingNaam: vestiging?.vestigingNaam ?? "",
            heeftKoffieThee: vestiging?.beschikbareFaciliteiten.includes(VestigingBeschikbareFaciliteiten.KOFFIE_THEE) ?? false,
            heeftToilet: vestiging?.beschikbareFaciliteiten.includes(VestigingBeschikbareFaciliteiten.TOILET) ?? false,
            isNsRetailVestiging: vestiging?.isNsRetailVestiging ?? false,
            inzetbaar: vestiging?.inzetbaar ?? false,
            telefoonnummer: vestiging?.telefoonnummer ?? "",
            opmerkingVestiging: vestiging?.opmerkingVestiging ?? "",
            openMaandag: vestiging?.openingstijden?.find((o) => o.dag === 1)?.openVan ?? "Gesloten",
            sluitMaandag: vestiging?.openingstijden?.find((o) => o.dag === 1)?.openTot ?? "Gesloten",
            openDinsdag: vestiging?.openingstijden?.find((o) => o.dag === 2)?.openVan ?? "Gesloten",
            sluitDinsdag: vestiging?.openingstijden?.find((o) => o.dag === 2)?.openTot ?? "Gesloten",
            openWoensdag: vestiging?.openingstijden?.find((o) => o.dag === 3)?.openVan ?? "Gesloten",
            sluitWoensdag: vestiging?.openingstijden?.find((o) => o.dag === 3)?.openTot ?? "Gesloten",
            openDonderdag: vestiging?.openingstijden?.find((o) => o.dag === 4)?.openVan ?? "Gesloten",
            sluitDonderdag: vestiging?.openingstijden?.find((o) => o.dag === 4)?.openTot ?? "Gesloten",
            openVrijdag: vestiging?.openingstijden?.find((o) => o.dag === 5)?.openVan ?? "Gesloten",
            sluitVrijdag: vestiging?.openingstijden?.find((o) => o.dag === 5)?.openTot ?? "Gesloten",
            openZaterdag: vestiging?.openingstijden?.find((o) => o.dag === 6)?.openVan ?? "Gesloten",
            sluitZaterdag: vestiging?.openingstijden?.find((o) => o.dag === 6)?.openTot ?? "Gesloten",
            openZondag: vestiging?.openingstijden?.find((o) => o.dag === 7)?.openVan ?? "Gesloten",
            sluitZondag: vestiging?.openingstijden?.find((o) => o.dag === 7)?.openTot ?? "Gesloten",
        },
    });

    const onSubmit: SubmitHandler<NewVestigingFormValues> = async (data) => {
        console.log({ data });
        const beschikbareFaciliteiten: VestigingBeschikbareFaciliteiten[] = [];
        if (data.heeftKoffieThee) beschikbareFaciliteiten.push(VestigingBeschikbareFaciliteiten.KOFFIE_THEE);
        if (data.heeftToilet) beschikbareFaciliteiten.push(VestigingBeschikbareFaciliteiten.TOILET);

        const openingstijden: Openingstijden[] = [];
        for (const day of DAYS) {
            if (data[`open${day}`] !== "Gesloten" && data[`sluit${day}`] !== "Gesloten") {
                openingstijden.push({ dag: DAYS.indexOf(day) + 1, openVan: data[`open${day}`], openTot: data[`sluit${day}`] });
            }
        }

        const vestigingData: UpsertVestigingDto = {
            bedrijfsNaam: data.bedrijfsNaam,
            vestigingNaam: data.vestigingNaam,
            stationCode: data.stationCode,
            telefoonnummer: data.telefoonnummer ?? "",
            opmerkingVestiging: data.opmerkingVestiging,
            beschikbareFaciliteiten,
            isNsRetailVestiging: data.isNsRetailVestiging,
            inzetbaar: data.inzetbaar,
            openingstijden,
        };

        try {
            setSubmitError(null);
            if (isEditing && vestiging) {
                await updateVestiging.mutateAsync({ id: vestiging.id, payload: vestigingData });
            } else {
                await createVestiging.mutateAsync(vestigingData);
            }
            onOpenChange(false);
        } catch (error) {
            setSubmitError(error as Error);
        }
    };
    return (
        <Form {...form}>
            <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="grid gap-5">
                <DialogBody>
                    <div className="grid grid-cols-2 gap-5">
                        <StationsAutocomplete
                            nlStationsOnly
                            form={form}
                            name="stationCode"
                            readOnly={isEditing && !handmatigToegevoegd}
                            data-testid="station-code"
                            initialValue={vestiging?.stationCode}
                        />
                        <FormFieldInput
                            form={form}
                            label="Telefoonnummer"
                            name="telefoonnummer"
                            placeholder="0612345678"
                            data-testid="telefoonnummer"
                        />
                        <FormFieldInput
                            form={form}
                            label={"Bedrijfsnaam"}
                            name="bedrijfsNaam"
                            placeholder="Kiosk"
                            readOnly={isEditing && !handmatigToegevoegd}
                            className="flex-1"
                            data-testid="bedrijfsnaam"
                        />
                        <FormFieldInput
                            form={form}
                            label={"Vestigingnaam"}
                            name="vestigingNaam"
                            placeholder="Kiosk - stationshal"
                            readOnly={isEditing && !handmatigToegevoegd}
                            className="flex-1"
                            data-testid="vestiging-naam"
                        />
                        <div className="col-span-2">
                            <TextAreaFormField
                                form={form}
                                label="Opmerking (optioneel)"
                                name="opmerkingVestiging"
                                placeholder="Opmerking"
                                data-testid="opmerking-vestiging"
                            />
                        </div>

                        <div className="grid gap-1">
                            <Label>Beschikbare faciliteiten</Label>
                            <CheckboxFormField form={form} label="Koffie en thee" name="heeftKoffieThee" readOnly={!handmatigToegevoegd} />
                            <CheckboxFormField
                                form={form}
                                label="Toilet"
                                name="heeftToilet"
                                readOnly={!handmatigToegevoegd && !form.getValues("heeftKoffieThee")}
                            />
                        </div>
                        <div className="grid gap-1">
                            <Label>Opties</Label>
                            <div className="grid w-fit gap-2">
                                <CheckboxFormField form={form} label="NS Retail vestiging" name="isNsRetailVestiging" readOnly={true} />
                                <CheckboxFormField form={form} label="Inzetbaar" name="inzetbaar" />
                            </div>
                        </div>
                    </div>
                    <div>
                        <Label className="">Openingstijden</Label>
                        <div className="grid grid-cols-12 items-center gap-2">
                            <Label className="col-span-2 col-start-4 px-2 text-center">Van</Label>
                            <Label className="col-span-2 col-start-6 px-2 text-center">Tot</Label>
                            {DAYS.map((day) => (
                                <OpeningHoursDay key={day} form={form} day={day} handmatigToegevoegd={handmatigToegevoegd} />
                            ))}
                        </div>
                    </div>

                    {submitError && (
                        <ErrorDisplay
                            error={submitError}
                            title={isEditing ? "Fout bij het bijwerken van vestiging" : "Fout bij het toevoegen van vestiging"}
                        />
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="primary"
                        type="submit"
                        className="justify-self-end"
                        disabled={form.formState.isSubmitting || createVestiging.isPending || updateVestiging.isPending}
                        data-testid="submit-vestiging"
                    >
                        {isEditing ? "Vestiging bijwerken" : "Vestiging toevoegen"}
                    </Button>
                </DialogFooter>
            </form>
        </Form>
    );
}
